import { useEffect, useState } from "react";
import { Sliders } from "lucide-react";
import styles from "./ProductDetailTuner.module.css";

export interface ProductTunerState {
  galleryRadius: number; // px
  panelGap: number;      // px
  heroMax: number;       // px — max width of the product image column
  swatchSize: number;    // px — diameter of color swatches
}

const DEFAULTS: ProductTunerState = {
  galleryRadius: 16,
  panelGap: 48,
  heroMax: 380,
  swatchSize: 24,
};

interface ProductDetailTunerProps {
  state: ProductTunerState;
  setState: (next: ProductTunerState) => void;
}

/** Visible only when `?tune=1` is in the URL. Per global rule:
 *  every visual-design feature ships with a live tuner. */
export default function ProductDetailTuner({ state, setState }: ProductDetailTunerProps) {
  const [open, setOpen] = useState(false);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    setVisible(new URLSearchParams(window.location.search).get("tune") === "1");
  }, []);

  if (!visible) return null;

  function set<K extends keyof ProductTunerState>(key: K, val: ProductTunerState[K]) {
    setState({ ...state, [key]: val });
  }

  return (
    <div className={styles.tuner}>
      <button
        className={styles.toggle}
        onClick={() => setOpen((v) => !v)}
        aria-label="Toggle product design tuner"
      >
        <Sliders size={12} />
        {open ? "Close" : "Tune"}
      </button>
      {open && (
        <div className={styles.panel}>
          <div className={styles.title}>Product Design Tuner</div>

          <Row label="Gallery radius" value={`${state.galleryRadius}px`}>
            <input
              className={styles.slider}
              type="range" min={0} max={40} step={1}
              value={state.galleryRadius}
              onChange={(e) => set("galleryRadius", Number(e.target.value))}
            />
          </Row>

          <Row label="Panel gap" value={`${state.panelGap}px`}>
            <input
              className={styles.slider}
              type="range" min={16} max={96} step={2}
              value={state.panelGap}
              onChange={(e) => set("panelGap", Number(e.target.value))}
            />
          </Row>

          <Row label="Image size" value={`${state.heroMax}px`}>
            <input
              className={styles.slider}
              type="range" min={240} max={560} step={10}
              value={state.heroMax}
              onChange={(e) => set("heroMax", Number(e.target.value))}
            />
          </Row>

          <Row label="Swatch size" value={`${state.swatchSize}px`}>
            <input
              className={styles.slider}
              type="range" min={16} max={40} step={1}
              value={state.swatchSize}
              onChange={(e) => set("swatchSize", Number(e.target.value))}
            />
          </Row>

          <button
            className={styles.reset}
            onClick={() => setState(DEFAULTS)}
          >
            Reset
          </button>
        </div>
      )}
    </div>
  );
}

function Row({
  label, value, children,
}: { label: string; value: string; children: React.ReactNode }) {
  return (
    <div className={styles.row}>
      <div className={styles.rowLabel}>
        <span>{label}</span>
        <span>{value}</span>
      </div>
      {children}
    </div>
  );
}

export { DEFAULTS as PRODUCT_TUNER_DEFAULTS };
